import {
  createSelector,
} from 'reselect';

import {
  userColors,
} from '../utils';

import type {
  Id,
  Resources,
  ResourceType,
  ResourceTypes,
  ResourceMeta,
  ResourceResourcesType,
} from '../types';

import {
  alphSortByName,
  alphSortByUserName,
} from './utils';

export const getResourceIds = <T extends ResourceType>(
  resourceName: T,
  listName: string,
) => (state: Resources): Id[] => (
    state[resourceName].lists[listName]
  );

export const getResourceMap = <T extends ResourceType>(resourceName: T) => (
  (state: Resources): ResourceResourcesType<T> => (
    state[resourceName].resources
  )
);

export const getResourceById = <T extends ResourceType>(
  resourceName: T,
  id: Id,
) => (
    (state: Resources): ResourceTypes[T] => (
      state[resourceName].resources[id]
    )
  );

export const getResourceMeta = <T extends ResourceType, M extends ResourceMeta>(
  resourceName: T,
  metaKey: M,
) => (
    (state: Resources): ResourceMeta[M] => (
      state[resourceName].meta[metaKey]
    )
  );

export const getSelectedIds = <T extends ResourceType>(
  resourceName: T,
) => (
    (state: Resources): Id[] => (
      state[resourceName].selectedIds || []
    )
  );

export const getRequestProperties = <T extends ResourceType>(
  resourceName: T,
  requestName: string,
) => (
    (state: Resources) => (
      state[resourceName].requests[requestName] || {}
    )
  );

export const getResourceLists = <T extends ResourceType>(
  resourceName: T,
  listName?: string,
) => (
    (state: Resources[]) => (listName
      ? (state[resourceName].lists[listName] || [])
      : state[resourceName].lists)
  );

const resourceSelectors: {[k: string]: any} = {};
export const getResourceMappedList = <T extends ResourceType>(
  resourceName: T,
  listName: string = 'all',
) => {
  const selectorKey = `${resourceName}_${listName}`;
  if (resourceSelectors[selectorKey]) {
    return resourceSelectors[selectorKey];
  }
  resourceSelectors[selectorKey] = (
    createSelector(
      [
        getResourceIds(resourceName, listName),
        getResourceMap(resourceName),
      ],
      (ids = [], map) => ids.map(id => map[id]),
    )
  );
  return resourceSelectors[selectorKey];
};

const selectionResourceSelectors: {[k: string]: any} = {};
export const getSelectionResourceMappedList = <T extends ResourceType>(
  resourceName: T,
  listName: string = 'all',
) => {
  const selectorKey = `${resourceName}_${listName}`;
  if (selectionResourceSelectors[selectorKey]) {
    return selectionResourceSelectors[selectorKey];
  }

  selectionResourceSelectors[selectorKey] = (
    createSelector(
      [
        getResourceMappedList(resourceName, listName),
        getSelectedIds(resourceName),
      ],
      (resources = [], ids = []) => resources.map(
        r => ({
          ...r,
          selected: ids.indexOf(r.id) !== -1,
        }),
      ),
    )
  );

  return selectionResourceSelectors[selectorKey];
};

export const getSelectedResourceMappedList = <T extends ResourceType>(
  resourceType: T,
  listName: string = 'all',
) => createSelector(
    getSelectionResourceMappedList(resourceType, listName),
    (resources) => resources.filter(resource => resource.selected),
  );

export const getResourceByProp = (
  resourceType: ResourceType,
  prop: string,
  value: any,
) => createSelector(
  getResourceMappedList(resourceType),
  (resources) => resources.find(resource => resource[prop] === value),
);

export const sortedSelectionResourceList = (
  resourceType: ResourceType,
  listName?: string,
) => createSelector(
  getSelectionResourceMappedList(resourceType, listName),
  (items: any) => {
    switch (resourceType) {
      case 'clients':
        return items
          .filter(item => !item.is_pro)
          .map(client => ({
            ...client,
            color: userColors[client.id % userColors.length],
          }))
          .sort(alphSortByUserName);
      case 'archivedClients':
        return items
          .filter(item => !item.is_pro)
          .map(client => ({
            ...client,
            color: userColors[client.id % userColors.length],
          }))
          .sort(alphSortByUserName);
      default:
        return items.sort(alphSortByName);
    }
  },
);
